import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './CategoryManager.css';

const TIPOS = ['receita', 'despesa'];
const COLORS = [
  '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

const CategoryManager = ({ onClose }) => {
  const { authFetch } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterTipo, setFilterTipo] = useState('todos');
  const [form, setForm] = useState({ nome: '', tipo: 'despesa', descricao: '', cor: '#3b82f6' });
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchCategories = async () => {
    try {
      const res = await authFetch('/api/categories');
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      showToast('Erro ao carregar categorias', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleFormChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openAdd = () => {
    setForm({ nome: '', tipo: 'despesa', descricao: '', cor: '#3b82f6' });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setForm({ nome: cat.nome, tipo: cat.tipo, descricao: cat.descricao || '', cor: cat.cor || '#3b82f6' });
    setEditingId(cat.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return showToast('Nome é obrigatório', 'error');

    try {
      if (editingId) {
        await authFetch(`/api/categories/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(form),
        });
        showToast('Categoria atualizada!');
      } else {
        await authFetch('/api/categories', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        showToast('Categoria criada!');
      }
      setShowForm(false);
      fetchCategories();
    } catch (err) {
      showToast('Erro ao salvar categoria', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await authFetch(`/api/categories/${id}`, { method: 'DELETE' });
      showToast('Categoria removida!');
      setDeleteConfirm(null);
      fetchCategories();
    } catch (err) {
      showToast('Erro ao remover categoria', 'error');
    }
  };

  const filtered = categories.filter(c => filterTipo === 'todos' || c.tipo === filterTipo);

  return (
    <div className="catmgr-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="catmgr-modal">
        {/* Toast */}
        {toast && (
          <div className={`catmgr-toast ${toast.type}`}>
            {toast.type === 'success' ? '✅' : '❌'} {toast.msg}
          </div>
        )}

        {/* Header */}
        <div className="catmgr-header">
          <div>
            <h2>🏷️ Categorias</h2>
            <p>{categories.length} categorias cadastradas</p>
          </div>
          <div className="catmgr-header-actions">
            <button className="catmgr-btn-add" onClick={openAdd}>+ Nova</button>
            <button className="modal-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Filtros */}
        <div className="catmgr-filters">
          {['todos', 'receita', 'despesa'].map(f => (
            <button
              key={f}
              className={`catmgr-filter ${filterTipo === f ? 'active' : ''}`}
              onClick={() => setFilterTipo(f)}
            >
              {f === 'todos' ? 'Todas' : f === 'receita' ? '📈 Receitas' : '📉 Despesas'}
            </button>
          ))}
        </div>

        {/* Lista */}
        <div className="catmgr-list">
          {loading ? (
            <div className="catmgr-empty">Carregando...</div>
          ) : filtered.length === 0 ? (
            <div className="catmgr-empty">
              <span>🏷️</span>
              <p>Nenhuma categoria encontrada</p>
              <button className="catmgr-btn-add" onClick={openAdd}>Criar primeira categoria</button>
            </div>
          ) : (
            filtered.map(cat => (
              <div key={cat.id} className="catmgr-item">
                <div className="catmgr-item-left">
                  <div
                    className="catmgr-color-dot"
                    style={{ background: cat.cor || (cat.tipo === 'receita' ? '#10b981' : '#ef4444') }}
                  />
                  <div className="catmgr-item-info">
                    <span className="catmgr-item-name">{cat.nome}</span>
                    {cat.descricao && <span className="catmgr-item-desc">{cat.descricao}</span>}
                  </div>
                </div>
                <div className="catmgr-item-right">
                  <span className={`catmgr-badge ${cat.tipo}`}>
                    {cat.tipo === 'receita' ? '📈 Receita' : '📉 Despesa'}
                  </span>
                  <button className="catmgr-btn-edit" onClick={() => openEdit(cat)} title="Editar">✏️</button>
                  <button className="catmgr-btn-delete" onClick={() => setDeleteConfirm(cat)} title="Excluir">🗑️</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal de formulário */}
      {showForm && (
        <div className="catmgr-form-overlay" onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className="catmgr-form-modal">
            <div className="catmgr-form-header">
              <h3>{editingId ? 'Editar Categoria' : 'Nova Categoria'}</h3>
              <button className="modal-close-btn" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} className="catmgr-form">
              <div className="catmgr-field">
                <label>Nome *</label>
                <input
                  name="nome"
                  type="text"
                  placeholder="Ex: Alimentação"
                  value={form.nome}
                  onChange={handleFormChange}
                  required
                  autoFocus
                />
              </div>
              <div className="catmgr-field">
                <label>Tipo *</label>
                <select name="tipo" value={form.tipo} onChange={handleFormChange}>
                  <option value="despesa">📉 Despesa</option>
                  <option value="receita">📈 Receita</option>
                </select>
              </div>
              <div className="catmgr-field">
                <label>Descrição</label>
                <input
                  name="descricao"
                  type="text"
                  placeholder="Opcional"
                  value={form.descricao}
                  onChange={handleFormChange}
                />
              </div>
              <div className="catmgr-field">
                <label>Cor</label>
                <div className="catmgr-colors">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`catmgr-color-btn ${form.cor === c ? 'selected' : ''}`}
                      style={{ background: c }}
                      onClick={() => setForm(p => ({ ...p, cor: c }))}
                    />
                  ))}
                </div>
              </div>
              <div className="catmgr-form-actions">
                <button type="button" className="catmgr-btn-cancel" onClick={() => setShowForm(false)}>Cancelar</button>
                <button type="submit" className="catmgr-btn-save">
                  {editingId ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmação de delete */}
      {deleteConfirm && (
        <div className="catmgr-form-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="catmgr-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="catmgr-confirm-icon">🗑️</div>
            <h3>Excluir categoria?</h3>
            <p>Tem certeza que deseja excluir <strong>"{deleteConfirm.nome}"</strong>?</p>
            <div className="catmgr-confirm-actions">
              <button className="catmgr-btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
              <button className="catmgr-btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
